import React, { useState, useMemo } from 'react';
import { contactApi, ContactForm } from '../../services/api';
import { cartService } from '../../services/cartService';
import SuccessModal from '../common/SuccessModal';
import './ContactForm.css';

const ContactFormComponent: React.FC = () => {
    const [formData, setFormData] = useState<ContactForm>({
        name: '',
        phone: '',
        email: '',
        message: ''
    });
    const [loading, setLoading] = useState(false);
    const [showSuccessModal, setShowSuccessModal] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const cartItemIds = useMemo(() => {
        const items = cartService.getCartItems();
        return Array.from(new Set(items.map((item) => item.productId)));
    }, []);

    const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        if (!formData.name.trim() || !formData.phone.trim()) {
            setError('Name and phone number are required');
            return;
        }

        const items = cartService.getCartItems();
        const ids = Array.from(new Set(items.map((item) => item.productId)));
        const messageWithCartIds = ids.length > 0
            ? [formData.message?.trim(), `Items in cart: ${ids.join(', ')}`].filter(Boolean).join('\n\n')
            : (formData.message?.trim() || '');

        try {
            setLoading(true);
            setError(null);
            await contactApi.submit({
                ...formData,
                message: messageWithCartIds || undefined
            });
            setShowSuccessModal(true);
            setFormData({
                name: '',
                phone: '',
                email: '',
                message: ''
            });
        } catch (err) {
            setError('Failed to submit form. Please try again.');
            console.error('Error submitting contact form:', err);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="contact-form-container">
            <div className="contact-form">
                <h3>Enquire Now</h3>
                <p>Get in touch with us for any inquiries about our products</p>

                <form onSubmit={handleSubmit}>
                    <div className="form-group">
                        <label htmlFor="name">Name *</label>
                        <input
                            type="text"
                            id="name"
                            name="name"
                            value={formData.name}
                            onChange={handleChange}
                            required
                            placeholder="Your full name"
                        />
                    </div>

                    <div className="form-group">
                        <label htmlFor="phone">Phone Number *</label>
                        <input
                            type="tel"
                            id="phone"
                            name="phone"
                            value={formData.phone}
                            onChange={handleChange}
                            required
                            placeholder="Your phone number"
                        />
                    </div>

                    <div className="form-group">
                        <label htmlFor="email">Email (Optional)</label>
                        <input
                            type="email"
                            id="email"
                            name="email"
                            value={formData.email}
                            onChange={handleChange}
                            placeholder="Your email address"
                        />
                    </div>

                    {cartItemIds.length > 0 && (
                        <div className="form-group form-group--cart-ids">
                            <label>Items in your cart (will be included in enquiry)</label>
                            <p className="cart-ids-list">{cartItemIds.join(', ')}</p>
                        </div>
                    )}
                    <div className="form-group">
                        <label htmlFor="message">Message (Optional)</label>
                        <textarea
                            id="message"
                            name="message"
                            value={formData.message}
                            onChange={handleChange}
                            placeholder="Tell us about your requirements"
                            rows={4}
                        />
                    </div>

                    {error && (
                        <div className="error-message">
                            {error}
                        </div>
                    )}

                    <button
                        type="submit"
                        className="btn btn-primary submit-btn"
                        disabled={loading}
                    >
                        {loading ? 'Submitting...' : 'Submit Inquiry'}
                    </button>
                </form>
            </div>
            <SuccessModal
                isOpen={showSuccessModal}
                onClose={() => setShowSuccessModal(false)}
                type="enquiry"
            />
        </div>
    );
};

export default ContactFormComponent;
